import periodicUpdater from './incidentViewportPeriodicUpdater';
import trafficIncidentCommons from './trafficIncidentCommons';
import constants from '../../core/constants';
import {incidentDetails} from 'Services/services/incidentDetails';
import { incidentDetailsResponseMerger } from './incidentDetailsResponseMerger';
import IncidentDetailsMarkersManager from './trafficIncidentDetailsMarkersManager';

export const ID = 'trafficIncidentsDetails';

function getZoom(map) {
    return Math.floor(map.getZoom());
}

/* eslint-disable */
/**
 * This is a tier for the Traffic Incident Details service.
 * @class TrafficIncidentDetailsTier
 * @private
 * @constructor
 * @param {Object} [options] Tier options.
 * @param {Boolean} [options.incidentDetails.diff] Flag indicating the use of the markers diff
 * algorithm to avoid removing all markers with each update.
 * @param {Object} [options.incidentDetails.markerOptions] Options used to
 * generate {{#crossLink "IncidentMarker"}}{{/crossLink}}.
 */
/* eslint-enable */
export class TrafficIncidentDetailsTier {
    constructor(options, position) {
        this.position = position;
        this.options = options;
        this.options.incidentDetails = options.incidentDetails || {};
        this.markerOptions = options.incidentDetails.markerOptions || {};
        this.updatePeriodicUpdater = true;
        this.trafficModelId = constants.VIEWPORT_DEFAULT_VALUE;
        this.map = null;

        periodicUpdater.set(this.options.refresh, this.options.key);
    }

    getId = () => ID;
    getPosition = () => this.position;
    getStyle = () => this.style;
    getOptions = () => this.options;
    _splitBoundingBox = (bBox) => {
        const minLon = bBox.getWest();
        const maxLon = bBox.getEast();
        const minLat = bBox.getSouth();
        const maxLat = bBox.getNorth();

        if (minLon < -180) {
            return [
                { minLon: -180, minLat: minLat, maxLon: maxLon, maxLat: maxLat },
                { minLon: 360 + minLon, minLat: minLat, maxLon: 180, maxLat: maxLat }
            ];
        }

        if (maxLon > 180) {
            return [
                { minLon: minLon, minLat: minLat, maxLon: 180, maxLat: maxLat },
                { minLon: -180, minLat: minLat, maxLon: maxLon - 360, maxLat: maxLat }
            ];
        }

        return [bBox];
    };

    _fetchIncidentDetails = () => {
        const requestOptions = {
            key: this.options.key,
            trafficModelID: this.trafficModelId,
            boundingBox: this.map.getBounds(),
            zoomLevel: getZoom(this.map),
            style: this.options.incidentDetails.style
        };

        const boundingBoxes = this._splitBoundingBox(this.map.getBounds());
        const promises = [];
        boundingBoxes.forEach(boundingBox => {
            promises.push(incidentDetails({...requestOptions, boundingBox}).go());
        });

        return Promise.all(promises)
            .then(responses => {
                return incidentDetailsResponseMerger(responses);
            });
    };

    _updateIncidentMarkers = async () => {
        const trafficIncidents = await this._fetchIncidentDetails();
        this.incidentsMarkerManager.addMarkers(trafficIncidents);
    };

    _refreshDetails = async (trafficModelId) => {
        this.trafficModelId = trafficModelId || this.trafficModelId;

        if (!this.trafficModelId || this.trafficModelId === constants.VIEWPORT_DEFAULT_VALUE) {
            return;
        }

        this.updatePeriodicUpdater = false;

        await this._update();

        this.updatePeriodicUpdater = true;
    };

    _mapEventHandler = () => {
        periodicUpdater.refreshTrafficModelIdAndExecuteCallbacks(ID);
    };

    _update = async () => {
        trafficIncidentCommons.deregisterMapEventsHandlers(this.map, this._mapEventHandler);

        await this._updateIncidentMarkers();

        trafficIncidentCommons.registerMapEventsHandlers(this.map, this._mapEventHandler);
    };

    onAdd = (map) => {
        this.map = map;
        this.incidentsMarkerManager = new IncidentDetailsMarkersManager(
            this.markerOptions,
            this.options.incidentDetails.diff,
            map
        );
        trafficIncidentCommons.registerMapEventsHandlers(this.map, this._mapEventHandler);

        periodicUpdater.addOnModelIdRetrievedCallback(ID, async (trafficModelId) => {
            await this._refreshDetails(trafficModelId);
        });
    };

    _show = async () => {
        if (this.updatePeriodicUpdater) {
            trafficIncidentCommons.registerMapEventsHandlers(this.map, this._mapEventHandler);
        }

        await this._updateIncidentMarkers();
    };

    _hide = () => {
        if (this.updatePeriodicUpdater) {
            trafficIncidentCommons.deregisterMapEventsHandlers(this.map, this._mapEventHandler);
        }

        this.incidentsMarkerManager.clearMarkers();
    };

    onRemove = (map) => {
        if (!this.map) {
            this.map = map;
        }
        periodicUpdater.removeOnModelIdRetrievedCallback(ID);
        trafficIncidentCommons.deregisterMapEventsHandlers(this.map, this._mapEventHandler);
        this.incidentsMarkerManager.clearMarkers();
        delete this.map;
    }
}

export default (options, position) => {
    return new TrafficIncidentDetailsTier(options, position);
};



// WEBPACK FOOTER //
// ./map/tier/trafficIncidentDetailsTier.js